import LoadingRow from "@/components/LoadingRow";
import { useRowSelection } from "@/contexts/RowSelection/useRowSelection";
import { useGetAllRecordings } from "@/hooks/queries/useGetAllRecordings";
import { useCustomParams } from "@/hooks/useCustomParams";
import { useRecordingList } from "@/hooks/useRecordingList";
import {
  BoldTextM,
  Breakpoint,
  Button,
  ButtonSeverity,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Color,
  HorizontalFlex,
  MediumTextM,
  Space,
} from "@enxcs/dap-ui-stratos";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BulkActionsBar } from "./styles";

type Props = {
  totalItems: number;
  hasPagination: boolean;
};

export function RecordingBulkActions({ totalItems, hasPagination }: Props) {
  const { t } = useTranslation();

  const { selecteds, onSelectAll, resetSelection, isSelectDisabled } = useRowSelection();

  const { onDeleteRow } = useRecordingList();

  const { orderByColumn, isAscending, searchTerm } = useCustomParams();

  const totalSelecteds = selecteds.size;
  const hasSelection = totalSelecteds > 0;

  const { data: allPagesData, isLoadingAllPages } = useGetAllRecordings({
    totalItems,
    orderByColumn,
    isAscending,
    searchTerm,
    enabled: hasSelection && hasPagination && !!totalItems,
  });

  const allPagesItems = useMemo(
    () =>
      (allPagesData?.items ?? [])
        .filter((recording) => !isSelectDisabled(recording))
        .map((recording) => ({ row: recording.recordingId, exports: new Set((recording.exports ?? []).map((e) => e.id)) })),
    [allPagesData, isSelectDisabled],
  );

  const isAllPagesSelected = allPagesItems.length > 0 && totalSelecteds === allPagesItems.length;

  useEffect(() => {
    resetSelection();
  }, [searchTerm, resetSelection]);

  const handleSelectAllPages = (value: boolean) => {
    if (!value) {
      resetSelection();
      return;
    }

    onSelectAll(allPagesItems, true);
  };

  return (
    <BulkActionsBar $justify="space-between" $align="center" $gap={Space.V8} $padX={Space.V24} $breakpoint={Breakpoint.M} $visible={hasSelection}>
      <HorizontalFlex $gap={Space.V32} $align="center" $breakpoint={Breakpoint.M}>
        <HorizontalFlex $gap={Space.V4}>
          <MediumTextM color={Color.Neutral.V60}>{t("recordingScreen.selectedItems")}:</MediumTextM>
          <BoldTextM>{totalSelecteds}</BoldTextM>
        </HorizontalFlex>

        {hasPagination &&
          (isLoadingAllPages ? (
            <LoadingRow />
          ) : (
            <Checkbox
              value={isAllPagesSelected}
              onChange={handleSelectAllPages}
              label={
                <HorizontalFlex $gap={Space.V4}>
                  <MediumTextM color={Color.Neutral.V60}>{t("recordingScreen.selectAllPages")}:</MediumTextM>
                  <BoldTextM>{allPagesItems.length}</BoldTextM>
                </HorizontalFlex>
              }
            />
          ))}
      </HorizontalFlex>

      <Button
        label={t("buttons.deleteSelected")}
        variant={ButtonVariant.SECONDARY}
        severity={ButtonSeverity.DANGER}
        size={ButtonSize.S}
        onClick={() => onDeleteRow()}
      />
    </BulkActionsBar>
  );
}
